import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { PageHeader } from '../components/PageHeader';
import { useToolRegistry } from '../hooks/useOps';
import type { AgentToolSpec, ToolSchemaProperty } from '../api/types';

const TIER_ORDER = ['read', 'auto_write', 'approval_gated'] as const;
const TIER_COLOR: Record<string, 'info' | 'warning' | 'secondary'> = {
  read: 'info',
  auto_write: 'warning',
  approval_gated: 'secondary',
};

function fieldType(p: ToolSchemaProperty): string {
  if (p.enum) return p.enum.join(' | ');
  if (p.type === 'array') return 'array';
  return p.type ?? 'any';
}

function ToolCard({ tool }: { tool: AgentToolSpec }) {
  const props = Object.entries(tool.inputSchema.properties ?? {});
  const required = new Set(tool.inputSchema.required ?? []);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700} fontFamily="monospace">
          {tool.name}
        </Typography>
        <Chip size="small" label={tool.tier} color={TIER_COLOR[tool.tier] ?? 'default'} variant="outlined" />
        <Chip size="small" label={`added ${tool.addedIn}`} variant="outlined" />
        {tool.deprecatedIn && (
          <Chip size="small" color="error" label={`deprecated ${tool.deprecatedIn}`} />
        )}
        <Box flexGrow={1} />
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {tool.method} {tool.path}
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {tool.description}
      </Typography>

      {props.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Inputs
          </Typography>
          <Stack sx={{ mt: 0.5 }} divider={<Divider flexItem />} gap={0.75}>
            {props.map(([name, p]) => (
              <Stack key={name} direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
                <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                  {name}
                </Typography>
                {required.has(name) ? (
                  <Chip size="small" color="error" variant="outlined" label="required" sx={{ height: 18 }} />
                ) : (
                  <Chip size="small" variant="outlined" label="optional" sx={{ height: 18 }} />
                )}
                <Typography variant="caption" color="primary" fontFamily="monospace">
                  {fieldType(p)}
                </Typography>
                {p.description && (
                  <Typography variant="caption" color="text.secondary">
                    — {p.description}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          No inputs.
        </Typography>
      )}
    </Paper>
  );
}

export function AgentToolsPage() {
  const { data, isLoading, error } = useToolRegistry();

  return (
    <Box>
      <PageHeader
        title="Agent tools"
        action={
          data && (
            <Chip color="primary" variant="outlined" label={`registry v${data.schemaVersion}`} />
          )
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        This is the canonical MCP contract an agent (Hermes, Claude Desktop, …) discovers through the
        boundary. The server enforces these tiers and tool scopes — the agent's prompt can't widen
        them. Use the tool names below when scoping a key on <strong>Agent access</strong>.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      {isLoading && <Typography color="text.secondary">Loading registry…</Typography>}

      {data &&
        TIER_ORDER.map((tier) => {
          const tools = data.tools.filter((t) => t.tier === tier);
          if (tools.length === 0) return null;
          return (
            <Box key={tier} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Chip size="small" label={tier} color={TIER_COLOR[tier] ?? 'default'} />
                <Typography variant="body2" color="text.secondary">
                  {data.tiers[tier]}
                </Typography>
              </Stack>
              <Stack gap={1.5}>
                {tools.map((t) => (
                  <ToolCard key={t.name} tool={t} />
                ))}
              </Stack>
            </Box>
          );
        })}
    </Box>
  );
}
